/**
 * Collection hub filter engine (chips + 4-axis panel + pills + sort), wired
 * per plans/20260820-horizon-rebuild/phase-09-collection-hub.md. Same proven
 * pattern as assets/afroyla-collection-filter.js (URLSearchParams grammar,
 * data-island fail-open matching, pushState commit) generalized to 5 axes —
 * kept as its OWN module rather than an edit to that file; see
 * sections/afroyla-collection-hub.liquid's top comment for why (that file's
 * root selector `[data-afl-collection-drop]` never appears on the same page
 * as this one's `[data-afl-collection-hub]`).
 *
 * ONE BEHAVIORAL DIFFERENCE from the drop's engine: every row/chip click here
 * COMMITS IMMEDIATELY (state machine, phase-09-collection-hub.md — "grid + N
 * update instantly, panel stays open"). There is no pending/Apply-batch step;
 * Close/✕/"Show N pieces" (assets/afroyla-collection-hub-panel.js) only close
 * the panel — nothing to submit, everything already applied live.
 *
 * ---- DEGRADED-MODE CONTRACT — read sections/afroyla-collection-hub.liquid's
 * top comment first. Two consequences that live here specifically:
 *   1. `matches()`/`countFor()` only ever see whatever page(s) the data
 *      island has accumulated (the CURRENT SSR page at mount, plus whatever
 *      Load-more has appended since) — never the true collection-wide total.
 *      H2 review ruling (falsifiable-claims law): while ANY axis is
 *      degraded/active, this file must not assert a number it cannot prove.
 *      So while `hasUnconfiguredActive()` is true: the result line renders
 *      with NO count (just "· Clear filter"), the Apply-label renders "Show
 *      pieces" with no N, and the empty line NEVER renders (a 0-count read
 *      off a partial island is not proof the true filtered set is empty).
 *      Card visibility narrowing itself stays — that's still correct, only
 *      the printed NUMBERS are unprovable. Numbers return automatically the
 *      moment every active axis is `data-sd-*-configured` (SSR/fetch-morph
 *      truth takes back over) — no code change required.
 *   2. `needsFetch()` — and therefore a real Section Rendering round trip —
 *      only fires for price/sort/page changes today (native), so the file
 *      never trusts a partial fetch to fix up a degraded axis; it recomputes
 *      client-side instead. The moment an axis's `data-sd-*-configured`
 *      attribute flips true, this same function starts fetching for it too,
 *      zero code change required.
 */
(function () {
  'use strict';

  if (window.__aflHubFilterInit) return;
  window.__aflHubFilterInit = true;

  var ROOT_SEL = '[data-afl-collection-hub]';
  var PARAM = {
    form: 'filter.p.m.custom.form_group',
    look: 'filter.p.m.custom.hair_look',
    msg: 'filter.p.m.custom.message',
    ships: 'filter.p.m.custom.ships_status'
  };
  var PRICE_LABELS = { u50: 'Under $50', p50_99: '$50–99', p100_149: '$100–149', p150plus: '$150+' };
  var LOOK_LABELS = { afro: 'Afro', headwrap: 'Headwrap', silver: 'Silver hair', locs: 'Locs', bald: 'Bald' };
  var MSG_LABELS = { empowerment: 'Empowerment', faith: 'Faith', self_love: 'Self love', beauty: 'Beauty', resilience: 'Resilience', mama: 'Mama' };
  var SHIPS_LABELS = { now: 'Ships now', preorder: 'Pre-order' };

  function qs(root, sel) { return root.querySelector(sel); }
  function qsa(root, sel) { return Array.prototype.slice.call(root.querySelectorAll(sel)); }

  /* ---- state <-> URL ------------------------------------------------------ */

  function stateFromSearch(search) {
    var p = new URLSearchParams(search);
    var lte = p.get('filter.v.price.lte');
    var gte = p.get('filter.v.price.gte');
    var price = '';
    if (lte && !gte && parseFloat(lte) <= 50) price = 'u50';
    else if (gte && lte && parseFloat(gte) >= 50 && parseFloat(gte) < 100 && parseFloat(lte) < 100) price = 'p50_99';
    else if (gte && lte && parseFloat(gte) >= 100 && parseFloat(lte) < 150) price = 'p100_149';
    else if (gte && !lte && parseFloat(gte) >= 150) price = 'p150plus';
    return {
      form: p.get(PARAM.form) || '',
      look: p.get(PARAM.look) || '',
      msg: p.get(PARAM.msg) || '',
      price: price,
      ships: p.get(PARAM.ships) || '',
      sort: p.get('sort_by') || ''
    };
  }

  function urlForState(collectionUrl, state) {
    var parts = [];
    if (state.form) parts.push(PARAM.form + '=' + encodeURIComponent(state.form));
    if (state.look) parts.push(PARAM.look + '=' + encodeURIComponent(state.look));
    if (state.msg) parts.push(PARAM.msg + '=' + encodeURIComponent(state.msg));
    if (state.price === 'u50') parts.push('filter.v.price.lte=49.99');
    else if (state.price === 'p50_99') parts.push('filter.v.price.gte=50&filter.v.price.lte=99.99');
    else if (state.price === 'p100_149') parts.push('filter.v.price.gte=100&filter.v.price.lte=149.99');
    else if (state.price === 'p150plus') parts.push('filter.v.price.gte=150');
    if (state.ships) parts.push(PARAM.ships + '=' + encodeURIComponent(state.ships));
    if (state.sort) parts.push('sort_by=' + encodeURIComponent(state.sort));
    return parts.length ? collectionUrl + '?' + parts.join('&') : collectionUrl;
  }

  function needsFetch(state, prev, ctx) {
    if (state.price !== prev.price || state.sort !== prev.sort) return true;
    if (ctx.sdForm && state.form !== prev.form) return true;
    if (ctx.sdLook && state.look !== prev.look) return true;
    if (ctx.sdMsg && state.msg !== prev.msg) return true;
    if (ctx.sdShips && state.ships !== prev.ships) return true;
    return false;
  }

  // Any axis Shopify doesn't recognize yet, if active, means the RENDERED grid
  // wasn't actually narrowed server-side — only then is client-side visibility
  // math/hiding meaningful (and safe: it stays additive to whatever price
  // already narrowed).
  function hasUnconfiguredActive(state, ctx) {
    return (!!state.form && !ctx.sdForm) || (!!state.look && !ctx.sdLook) || (!!state.msg && !ctx.sdMsg) || (!!state.ships && !ctx.sdShips);
  }

  /* ---- island matching ----------------------------------------------------- */

  function matches(item, state) {
    if (state.form && item.form !== state.form) return false;
    if (state.look && item.look !== state.look) return false;
    if (state.msg && item.msg !== state.msg) return false;
    if (state.price === 'u50' && !(item.price < 50)) return false;
    if (state.price === 'p50_99' && !(item.price >= 50 && item.price < 100)) return false;
    if (state.price === 'p100_149' && !(item.price >= 100 && item.price < 150)) return false;
    if (state.price === 'p150plus' && !(item.price >= 150)) return false;
    if (state.ships === 'now' && item.now !== true) return false;
    if (state.ships === 'preorder' && item.now !== false) return false;
    return true;
  }

  function countFor(items, state) {
    var n = 0;
    for (var i = 0; i < items.length; i++) if (matches(items[i], state)) n++;
    return n;
  }

  function syncPill(ctx, axis, value, label) {
    var pill = qs(ctx.root, '[data-afl-hub-pill="' + axis + '"]');
    if (!pill) return;
    if (!value) {
      pill.hidden = true;
      return;
    }
    pill.hidden = false;
    var textEl = pill.querySelector('[data-afl-hub-pill-text]');
    if (textEl) textEl.textContent = label;
    var next = Object.assign({}, ctx.state);
    next[axis] = '';
    pill.href = urlForState(ctx.collectionUrl, next);
  }

  /* ---- render (idempotent) -------------------------------------------------- */

  function render(ctx, state) {
    var degraded = hasUnconfiguredActive(state, ctx);
    var anyFilter = !!(state.form || state.look || state.msg || state.price || state.ships);

    qsa(ctx.root, '[data-afl-hub-card]').forEach(function (cell) {
      if (!degraded) {
        cell.hidden = false;
        return;
      }
      var handle = cell.getAttribute('data-handle');
      var item = ctx.itemsByHandle[handle];
      // Cards beyond the island's cap can't be evaluated client-side — fail
      // open (never hide a card we have no data for) rather than lie.
      cell.hidden = item ? !matches(item, state) : false;
    });

    qsa(ctx.root, '[data-afl-hub-chip]').forEach(function (chip) {
      var active = (chip.getAttribute('data-form-value') || '') === state.form;
      chip.classList.toggle('is-active', active);
      chip.setAttribute('aria-current', active ? 'true' : 'false');
    });

    qsa(ctx.root, '[data-afl-hub-row]').forEach(function (row) {
      var axis = row.getAttribute('data-axis');
      var value = row.getAttribute('data-value');
      var input = row.querySelector('[data-afl-hub-input]');
      if (!input) return;
      var shouldCheck = state[axis] === value;
      if (input.checked !== shouldCheck) input.checked = shouldCheck;
      if (input.type === 'radio') input.dataset.wasChecked = shouldCheck ? 'true' : 'false';
    });

    syncPill(ctx, 'look', state.look, LOOK_LABELS[state.look]);
    syncPill(ctx, 'msg', state.msg, MSG_LABELS[state.msg]);
    syncPill(ctx, 'price', state.price, PRICE_LABELS[state.price]);
    syncPill(ctx, 'ships', state.ships, SHIPS_LABELS[state.ships]);

    var resultEl = qs(ctx.root, '[data-afl-hub-result]');
    if (resultEl) resultEl.hidden = !anyFilter;
    var tailEl = qs(ctx.root, '[data-afl-hub-tail]');
    // data-page1 can arrive empty from a Section Rendering swap — treat only
    // an explicit "false" as not-page-1, failing open on page 1.
    if (tailEl) tailEl.hidden = anyFilter || tailEl.getAttribute('data-page1') === 'false';

    var loadEl = qs(ctx.root, '[data-afl-hub-loadmore]');
    if (loadEl) {
      // Load more is unreliable once a page's worth of cards may have been
      // client-hidden (see top comment #1) — force it off for any
      // unconfigured-axis combo; otherwise defer to native/fetch truth.
      loadEl.hidden = degraded ? true : !ctx.hasMorePages;
    }

    if (degraded) {
      // H2 review ruling — falsifiable-claims law: the data island only ever
      // covers whatever page(s) have been rendered/appended so far, never the
      // true collection-wide count, while a degraded axis narrows client-side.
      // A number computed from that partial set (an item count, an "of N",
      // a "0 results") LOOKS live but can be wrong for a scoped combination —
      // worse than printing nothing. Suppress every numeric claim instead;
      // real numbers come back automatically once every active axis is
      // `data-sd-*-configured` (this whole `if (degraded)` branch stops
      // running and the SSR/fetch-morph numbers set elsewhere stand).
      var resultText = qs(ctx.root, '[data-afl-hub-result-text]');
      if (resultText) resultText.textContent = '·'; // "·" only — "Clear filter" link follows in the markup
      var emptyEl = qs(ctx.root, '[data-afl-hub-empty]');
      if (emptyEl) emptyEl.hidden = true; // never assert "no pieces fit" without a provable count
      qsa(ctx.root, '[data-afl-hub-apply-label]').forEach(function (btn) {
        btn.textContent = 'Show pieces';
      });
    }
  }

  /* ---- commit: pushState + render + (maybe) section fetch ------------------- */

  function applyState(ctx, state, pushUrl) {
    var prev = ctx.state;
    ctx.state = state;
    ctx.page = 1;
    if (pushUrl) {
      var url = urlForState(ctx.collectionUrl, state);
      history.pushState({ afroylaHub: state }, '', url);
    }
    render(ctx, state);
    document.dispatchEvent(new CustomEvent('filter:update', { detail: { state: state } }));
    if (needsFetch(state, prev, ctx)) ctx.fetchAndMorph(ctx, state, 1, false);
  }

  function commit(ctx, state) {
    applyState(ctx, state, true);
  }

  /* ---- init ------------------------------------------------------------------ */

  function wireAxisInput(ctx, input, row) {
    var axis = row.getAttribute('data-axis');
    var value = row.getAttribute('data-value');
    if (input.type === 'radio') {
      input.dataset.wasChecked = input.checked ? 'true' : 'false';
      input.addEventListener('click', function () {
        if (input.dataset.wasChecked === 'true') {
          input.checked = false;
          input.dataset.wasChecked = 'false';
          var next = Object.assign({}, ctx.state);
          next[axis] = '';
          commit(ctx, next);
        }
      });
      input.addEventListener('change', function () {
        qsa(ctx.root, 'input[name="' + input.name + '"]').forEach(function (sib) {
          sib.dataset.wasChecked = sib.checked ? 'true' : 'false';
        });
        var next = Object.assign({}, ctx.state);
        next[axis] = value;
        commit(ctx, next);
      });
    } else {
      input.addEventListener('change', function () {
        if (input.checked) {
          qsa(ctx.root, 'input[name="' + input.name + '"]').forEach(function (sib) {
            if (sib !== input) sib.checked = false;
          });
        }
        var next = Object.assign({}, ctx.state);
        next[axis] = input.checked ? value : '';
        commit(ctx, next);
      });
    }
  }

  function init(root) {
    if (root.__afroylaHub) return;
    root.__afroylaHub = true;

    var islandEl = root.querySelector('[data-afl-drop-island]');
    var items = [];
    try {
      items = islandEl ? JSON.parse(islandEl.textContent) || [] : [];
    } catch (e) {
      items = [];
    }
    var itemsByHandle = {};
    items.forEach(function (item) {
      itemsByHandle[item.handle] = item;
    });

    var loadEl0 = root.querySelector('[data-afl-hub-loadmore]');

    var ctx = {
      root: root,
      items: items,
      itemsByHandle: itemsByHandle,
      collectionUrl: root.getAttribute('data-collection-url'),
      sectionId: root.getAttribute('data-section-id'),
      pageSize: parseInt(root.getAttribute('data-page-size'), 10) || 24,
      sdForm: root.getAttribute('data-sd-form-configured') === 'true',
      sdLook: root.getAttribute('data-sd-look-configured') === 'true',
      sdMsg: root.getAttribute('data-sd-msg-configured') === 'true',
      sdShips: root.getAttribute('data-sd-ships-configured') === 'true',
      hasMorePages: loadEl0 ? !loadEl0.hidden : false,
      page: 1,
      state: null,
      fetchSeq: 0,
      render: render,
      matches: matches,
      countFor: countFor,
      urlForState: urlForState,
      commit: commit
    };
    root.__afroylaHubCtx = ctx;

    var initial = stateFromSearch(location.search);
    ctx.state = initial;
    render(ctx, initial);

    qsa(root, '[data-afl-hub-chip]').forEach(function (chip) {
      chip.addEventListener('click', function (e) {
        e.preventDefault();
        commit(ctx, Object.assign({}, ctx.state, { form: chip.getAttribute('data-form-value') || '' }));
      });
    });

    qsa(root, '[data-afl-hub-row]').forEach(function (row) {
      var input = row.querySelector('[data-afl-hub-input]');
      if (input) wireAxisInput(ctx, input, row);
    });

    // Pill removal + Clear filter are delegated on `root` (LOW review fix) —
    // a price/sort Section Rendering fetch replaces `[data-afl-hub-result]`
    // wholesale (afroyla-collection-hub-panel.js's non-append swap), which
    // would otherwise leave a freshly-swapped-in "Clear filter" link with no
    // listener and fall back to a full page navigation.
    root.addEventListener('click', function (e) {
      var pill = e.target.closest && e.target.closest('[data-afl-hub-pill]');
      if (pill && !pill.hidden) {
        e.preventDefault();
        var axis = pill.getAttribute('data-afl-hub-pill');
        var next = Object.assign({}, ctx.state);
        next[axis] = '';
        commit(ctx, next);
        return;
      }
      var clear = e.target.closest && e.target.closest('[data-afl-hub-clear-filter]');
      if (clear) {
        e.preventDefault();
        commit(ctx, { form: '', look: '', msg: '', price: '', ships: '', sort: ctx.state.sort });
      }
    });

    qsa(root, '[data-afl-hub-sort-select]').forEach(function (select) {
      select.addEventListener('change', function () {
        commit(ctx, Object.assign({}, ctx.state, { sort: select.value }));
      });
    });

    window.addEventListener('popstate', function () {
      var next = stateFromSearch(location.search);
      var prev = ctx.state;
      ctx.state = next;
      ctx.page = 1;
      render(ctx, next);
      if (needsFetch(next, prev, ctx)) ctx.fetchAndMorph(ctx, next, 1, false);
    });
  }

  document.querySelectorAll(ROOT_SEL).forEach(init);

  if (window.MutationObserver) {
    new MutationObserver(function (records) {
      for (var i = 0; i < records.length; i++) {
        var added = records[i].addedNodes;
        for (var j = 0; j < added.length; j++) {
          var node = added[j];
          if (node.nodeType !== 1) continue;
          if (node.matches && node.matches(ROOT_SEL)) init(node);
          else if (node.querySelector && node.querySelector(ROOT_SEL)) node.querySelectorAll(ROOT_SEL).forEach(init);
        }
      }
    }).observe(document.documentElement, { childList: true, subtree: true });
  }
})();
